import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import TextField from "@/common/components/TextField";
import { FormTransformers } from "@/common/utils/formTransformers";
import { branchHierarchyForUserAccess, branchHierarchyForUserAccessOptions } from "@/features/privateCompanies/constants/companyBranches/branchHierarchyForUserAccess.constant";
import { useUpdateCompanyBranchUserAccesses } from "@/features/privateCompanies/hooks/companyBranches/useUpdateCompanyBranchUserAccesses";
import { clientUserBranchAccessSchema, ClientUserBranchAccessValidationSchema } from "@/features/privateCompanies/schemas/companyBranches/updateUserAccesses.schema";
import { ClientUserBranchAccessesTransformer } from "@/features/privateCompanies/services/clientUserBranchAccessesTransformer";
import { ClientUserBranchAccess } from "@/features/privateCompanies/types/clientUserBranchAccess.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { FileEditIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

interface CompanyBranchUserAccessesUpdateProps {
  userBranchAccess: ClientUserBranchAccess;
}

const CompanyBranchUserAccessesUpdate = ({ userBranchAccess }: CompanyBranchUserAccessesUpdateProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ClientUserBranchAccessValidationSchema>({
    resolver: zodResolver(clientUserBranchAccessSchema),
    defaultValues: {
      user: {
        label: userBranchAccess.user.full_name,
        value: userBranchAccess.user.id,
      },
      branch_hierarchy_for_user_access: {
        label: branchHierarchyForUserAccess[userBranchAccess.branch_hierarchy_for_user_access],
        value: userBranchAccess.branch_hierarchy_for_user_access,
      },
    }
  });

  const {
    mutate: updateCompanyBranchUserAccesses,
    isPending: updateCompanyBranchUserAccessesIsPending,
  } = useUpdateCompanyBranchUserAccesses({
    userBranchAccess,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientUserBranchAccesses'] });
      setIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
        transformer: ClientUserBranchAccessesTransformer.errorsTransformer(),
      })(error);
    },
  });

  const onSubmit = (data: ClientUserBranchAccessValidationSchema) => {
    updateCompanyBranchUserAccesses(data);
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        user: {
          label: userBranchAccess.user.full_name,
          value: userBranchAccess.user.id,
        },
        branch_hierarchy_for_user_access: {
          label: branchHierarchyForUserAccess[userBranchAccess.branch_hierarchy_for_user_access],
          value: userBranchAccess.branch_hierarchy_for_user_access,
        },
      });
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            variant="link-white"
            operationKlassToCheckPermission="Admin::Client::CompanyBranches::UpdateUserAccesses"
          >
            <FileEditIcon strokeWidth={1.5} className="text-blue-800" />
          </Button>
        )}
        bodyClassName='max-h-[calc(90vh-72px-48px)] overflow-auto'
        title="Acesso a filial"
        titleClass="text-xl"
        useCancellableFooter
        cancelButtonClassName="bg-white-200 rounded-es-lg"
        confirmButtonClassName="bg-blue-700 rounded-ee-lg"
        confirmButtonIsLoading={updateCompanyBranchUserAccessesIsPending}
        disabled={updateCompanyBranchUserAccessesIsPending}
        showClose={false}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form className="flex flex-col gap-4 text-white-800" onSubmit={handleSubmit(onSubmit)}>
            <TextField
              label="Usuário"
              value={userBranchAccess.user.full_name}
              disabled
            />

            <SelectController
              control={control}
              name="branch_hierarchy_for_user_access"
              label="Permissão"
              options={branchHierarchyForUserAccessOptions}
              errorMessage={errors.branch_hierarchy_for_user_access?.message || undefined}
              isDisabled={updateCompanyBranchUserAccessesIsPending}
              markAsRequired
            />
          </form>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  )
}

export default CompanyBranchUserAccessesUpdate;
